import React from 'react';
import { Plus, X, RotateCcw } from 'lucide-react';

/**
 * La saisie d'un horaire : une liste de créneaux, matin et après-midi séparés.
 *
 * **Écrite une fois** et posée aux deux endroits qui la demandent — le réglage
 * de la maison et la formation : deux saisies du même objet auraient fini par
 * ne pas valider pareil, et c'est un horaire qui s'imprime sur une pièce
 * contractuelle.
 *
 * **La pause déjeuner n'est pas du temps de formation** : deux créneaux
 * plutôt qu'un, sinon la durée portée sur un document signé se gonflerait
 * d'une heure.
 *
 * Elle ne compose aucune phrase : c'est le serveur qui rend les lignes
 * imprimées, et les recomposer ici ferait une seconde définition de la règle
 * — l'écran finirait par annoncer autre chose que le document.
 */
export default function ChampHoraires({ value, onChange, reference, aide, testId }) {
  const creneaux = Array.isArray(value) ? value : [];

  const poser = (i, champ, v) => {
    const suite = creneaux.map((c, n) => (n === i ? { ...c, [champ]: v } : c));
    onChange(suite);
  };
  const ajouter = () => onChange([...creneaux, { debut: '', fin: '' }]);
  const retirer = (i) => onChange(creneaux.filter((_, n) => n !== i));

  // Le réglage se reprend tant qu'on ne l'a pas décroché : même forme que la
  // présaisie d'un prix depuis un catalogue — il propose, il n'impose pas.
  const refLisible = (reference || [])
    .filter((c) => c && c.debut && c.fin)
    .map((c) => `${c.debut} - ${c.fin}`)
    .join(' et ');
  const memeQueLaReference =
    JSON.stringify(creneaux.filter((c) => c && (c.debut || c.fin))) ===
    JSON.stringify((reference || []).filter((c) => c && (c.debut || c.fin)));

  return (
    <div data-testid={testId}>
      <div className="space-y-2">
        {creneaux.length === 0 && (
          <p className="text-[12.5px] text-[#8E8FA5] italic">
            Aucun horaire — rien ne s’imprimera sur les documents.
          </p>
        )}
        {creneaux.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="time"
              value={c?.debut || ''}
              onChange={(e) => poser(i, 'debut', e.target.value)}
              className="h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
            <span className="text-[13px] text-[#8E8FA5]">→</span>
            <input
              type="time"
              value={c?.fin || ''}
              onChange={(e) => poser(i, 'fin', e.target.value)}
              className="h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
            <button
              type="button"
              onClick={() => retirer(i)}
              title="Retirer ce créneau"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-[#8E8FA5] hover:text-[#E11D48] hover:bg-[#FFF1F2] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-2.5">
        <button
          type="button"
          onClick={ajouter}
          className="h-8 px-2.5 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px] font-medium text-[#4A4B63] flex items-center gap-1.5 hover:border-[#C7D2FE] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter un créneau
        </button>
        {refLisible && !memeQueLaReference && (
          <button
            type="button"
            onClick={() => onChange((reference || []).map((c) => ({ ...c })))}
            className="h-8 px-2.5 rounded-lg text-[12.5px] font-medium text-[#4F46E5] flex items-center gap-1.5 hover:bg-[#EEF2FF] transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reprendre l’horaire type ({refLisible})
          </button>
        )}
      </div>

      {aide && <p className="text-[12px] text-[#8E8FA5] mt-2">{aide}</p>}
    </div>
  );
}
